import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { FormDialog } from '@lumapps/lumx-forms/components/FormDialog';
import { mdiCalendarEnd, mdiCalendarStart } from '@lumapps/lumx/icons';
import { FlexBox, Heading, Icon, Text } from '@lumapps/lumx/react';
import {
    getSelectedDateTimeZone,
    getTimestampFromDateTimeZone,
} from '@lumapps/pickers/components/DateTimeZonePickerField/utils';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { useId } from '@lumapps/utils/hooks/useId';
import { getCurrentTimezone } from '@lumapps/utils/timezones/getCurrentTimezone';

import { CONTENT_DATE_FIELD_NAMES } from '../../constants';
import { useCurrentDates } from '../../hooks/useCurrentDates';
import { DESIGNER } from '../../keys';
import type { ScheduleFormValues } from '../../types';
import { FormContentDateField } from '../FormContentDateField';

export interface ContentScheduleDialogProps {
    isOpen?: boolean;
    isLoading?: boolean;
    onClose?: () => void;
    onConfirm: ({ startDate, endDate }: { startDate?: string; endDate?: string }) => void;
}

const CLASSNAME = 'content-schedule-dialog';

/**
 *  Dialog on content edition allowing to set a schedule and an expiration date on a content
 */
export const ContentScheduleDialog: React.FC<ContentScheduleDialogProps> = ({
    isOpen = false,
    onClose,
    onConfirm,
    isLoading,
}) => {
    const { block } = useClassnames(CLASSNAME);
    const { translateKey } = useTranslate();

    const { currentStartDate, currentEndDate } = useCurrentDates();

    const headingId = useId();
    const currentTimeZone = getCurrentTimezone();

    const formDefaultValues = React.useMemo(() => {
        const scheduleDateTimeZone = getSelectedDateTimeZone({
            timestamp: currentStartDate,
            timeZone: currentTimeZone,
            shouldFallbackToCurrentDateTime: false,
        });
        const scheduleDateTimestamp = getTimestampFromDateTimeZone(scheduleDateTimeZone);

        let endDateDefaultValue;

        if (currentEndDate) {
            const expirationDateTimeZone = getSelectedDateTimeZone({
                timestamp: currentEndDate,
                timeZone: currentTimeZone,
                shouldFallbackToCurrentDateTime: false,
            });
            const expirationDateTimestamp = getTimestampFromDateTimeZone(expirationDateTimeZone);
            endDateDefaultValue = { expirationDateTimeZone, expirationDateTimestamp };
        }

        return {
            [CONTENT_DATE_FIELD_NAMES.SCHEDULED]: {
                timestamp: scheduleDateTimestamp,
                data: scheduleDateTimeZone,
            },
            ...(endDateDefaultValue
                ? {
                      [CONTENT_DATE_FIELD_NAMES.EXPIRED]: {
                          timestamp: endDateDefaultValue.expirationDateTimestamp,
                          data: endDateDefaultValue.expirationDateTimeZone,
                      },
                  }
                : {}),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStartDate, currentTimeZone, currentEndDate]);

    const onSubmit = (formData: ScheduleFormValues) => {
        const contentDates = {
            startDate: formData[CONTENT_DATE_FIELD_NAMES.SCHEDULED].timestamp,
            endDate: formData[CONTENT_DATE_FIELD_NAMES.EXPIRED]?.timestamp,
        };

        onConfirm(contentDates);
    };

    return (
        <FormDialog
            className={block()}
            isOpen={isOpen}
            onSubmit={onSubmit}
            onClose={onClose}
            scope={CLASSNAME}
            headingId={headingId}
            dialogProps={{ isLoading }}
            heading={
                <Heading typography="title" as="h2" id={headingId}>
                    {translateKey(GLOBAL.SCHEDULE)}
                </Heading>
            }
            formActionProps={{
                saveProps: {
                    children: translateKey(GLOBAL.SCHEDULE),
                },
                cancelProps: {
                    children: translateKey(GLOBAL.CANCEL),
                },
            }}
            form={{
                defaultValues: formDefaultValues,
            }}
        >
            <FlexBox gap="huge" orientation="vertical">
                <FlexBox gap="big" orientation="vertical">
                    <div>
                        <FlexBox orientation="horizontal" gap="regular" hAlign="center">
                            <Icon icon={mdiCalendarStart} color="dark" colorVariant="L2" />
                            <Heading as="h3" typography="subtitle2">
                                {translateKey(GLOBAL.PUBLICATION)}
                            </Heading>
                        </FlexBox>
                        <Text as="p" typography="caption" color="dark" colorVariant="L1">
                            {translateKey(DESIGNER.PUBLICATION_DATE_HELPER)}
                        </Text>
                    </div>
                    <FormContentDateField fieldName={CONTENT_DATE_FIELD_NAMES.SCHEDULED} scope={CLASSNAME} />
                </FlexBox>
                <FlexBox gap="big" orientation="vertical">
                    <div>
                        <FlexBox orientation="horizontal" gap="regular" hAlign="center">
                            <Icon icon={mdiCalendarEnd} color="dark" colorVariant="L2" />
                            <Heading as="h3" typography="subtitle2">
                                {translateKey(GLOBAL.EXPIRATION)}
                            </Heading>
                        </FlexBox>
                        <Text as="p" typography="caption" color="dark" colorVariant="L1">
                            {translateKey(DESIGNER.EXPIRATION_DATE_HELPER)}
                        </Text>
                    </div>
                    <FormContentDateField fieldName={CONTENT_DATE_FIELD_NAMES.EXPIRED} scope={CLASSNAME} />
                </FlexBox>
            </FlexBox>
        </FormDialog>
    );
};
